import styled from 'styled-components';
import React from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Toast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import Button from 'design-system/src/components/Button/Button';
import Warning from 'design-system/src/assets/icons/warning.svg?react';

import { client, TremfyaExpressEnrollmentResponse } from '../api';
import { GeneralInfoTable } from 'design-system/src/components/GeneralInfoTable/GeneralInfoTable';
import TremfyaExpressEnrollmentCoreForm from '../components/tasks/tremfyaExpressEnrollmentTask/TremfyaExpressEnrollmentCoreForm';
import { TopNavbar } from '../components/TopNavbar/TopNavbar';
import { BusinessRelationshipInfoBox } from '../components/businessRelationshipNotices/BusinessRelationshipInfoBox/BusinessRelationshipInfoBox';
import { AuthContext } from '../auth';
import { ValuePropBanner } from '../components/tasks/tremfyaExpressEnrollmentTask/sharedStyles';

const Container = styled.div`
  box-sizing: border-box;
  min-height: 100vh;
  min-height: 100dvh;
  height: 100%;
  background-color: var(--surface-100);
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const BodyContainer = styled.div`
  box-sizing: border-box;
  max-width: var(--main-content-width);
  padding: 20px;
  margin: 0 auto;
  background-color: var(--white);
  border: 1px solid var(--grayscale-100);
  border-radius: var(--border-radius-small);
`;

const StyledBusinessRelationshipNotice = styled(BusinessRelationshipInfoBox)`
  margin-bottom: 16px;
`;

// TODO: share with TremfyaEnrollmentSavingsProgramSection.tsx
const ErrorBox = styled.div`
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: var(--border-radius-small);
  // TODO(tokens): Replace legacy color token --extra-light-red with numbered equivalent once available.
  background: var(--extra-light-red);
  margin-bottom: 16px;
`;

const ErrorBoxIcon = styled(Warning)`
  color: var(--red-400);
`;

const ErrorBoxContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--red-800);
`;

const LoginCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  background-color: var(--white);
  padding: 32px 24px;
  border: 1px solid var(--grayscale-100);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/**
 * Version of TremfyaExpressEnrollmentTaskView that is used for logged out users.
 *
 * This is used by practices that don't use the partner portal for tasking and instead use an
 * alternative like EMA. They will receive a message in their tasking system with a link to this
 * page, which they will view while (probably) logged out of the partner portal.
 */
const TremfyaExpressEnrollmentLoggedOutTaskPage: React.FC = () => {
  const authContext = React.useContext(AuthContext);
  const params = useParams();
  const token = params.token!;

  // TODO: move to utils, use in logged out header login button
  const loginHref = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;

  const queryClient = useQueryClient();
  const tremfyaExpressEnrollmentTaskQuery = useQuery<TremfyaExpressEnrollmentResponse>({
    queryKey: ['tremfya-enrollment', token],
    queryFn: async () => {
      const res = await client.get(`tremfya-enrollment/${token}`);
      return res.data;
    },
  });

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['tremfya-enrollment', token],
    });
    toast.custom(() => <Toast variant="success">Enrollment submitted</Toast>);
  };

  let body;
  if (tremfyaExpressEnrollmentTaskQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (tremfyaExpressEnrollmentTaskQuery.error) {
    body = <PageLoadError />;
  } else if (tremfyaExpressEnrollmentTaskQuery.data) {
    const { form_data, drug, status, show_redraft_banner } = tremfyaExpressEnrollmentTaskQuery.data;
    const isFormCompleted = status !== 'draft';
    const showCompletedForm = isFormCompleted && authContext.auth && form_data;
    const showDraftForm = !isFormCompleted && form_data;

    body = (
      <BodyContainer>
        <StyledBusinessRelationshipNotice variant="banner" />

        <Typography styledAs="h6" renderedAs="h1" marginBottom="1rem">
          Tremfya Express Enrollment form
        </Typography>

        <GeneralInfoTable
          patientName={drug.patient_name}
          prescriberName={drug.prescriber_name}
          dob={drug.dob}
          prescribedOn={drug.prescription_recieved_at}
          drugName={drug.name}
          prescriptionId={drug.id}
        />

        {/* It's glaring to have both a red and purple box -- prioritize the error box if applicable */}
        {show_redraft_banner ? (
          <ErrorBox>
            <ErrorBoxIcon />
            <ErrorBoxContent>
              <Typography styledAs="bodySmallDMSans">
                Something went wrong while processing the submission below. Please review and revise. A separate task
                was sent earlier with more details.
              </Typography>
            </ErrorBoxContent>
          </ErrorBox>
        ) : (
          <>
            <ValuePropBanner>
              <Typography>This is a digital enrollment form for a patient support program for Tremfya.</Typography>
            </ValuePropBanner>

            <Typography styledAs="bodySmallDMSans" marginBottom="24px">
              Please complete the digital form below to enroll your patient in the support program, which provides nurse
              guide support and access to a copay savings card, if eligible. Note this form does not require any
              signatures.
            </Typography>
          </>
        )}

        {isFormCompleted && !authContext.auth && (
          <LoginCard>
            <Typography styledAs="bodyLargeSpaceGrotesk" weight={500} marginBottom="16px">
              Log in to view the completed form
            </Typography>
            <Typography styledAs="bodySmallDMSans" marginBottom="24px">
              To protect patient privacy, you must be logged in to view this form in detail.
            </Typography>
            <Button size="sm" href={loginHref}>
              Log in
            </Button>
          </LoginCard>
        )}

        {/* we always show the form if it's in draft, and only logged in users can see the completed form */}
        {(showCompletedForm || showDraftForm) && (
          <>
            {isFormCompleted && (
              <Typography styledAs="bodyLargeSpaceGrotesk" weight={500} renderedAs="h2" marginBottom="16px">
                Below is the completed enrollment:
              </Typography>
            )}

            <TremfyaExpressEnrollmentCoreForm
              drug={drug}
              formData={form_data}
              submissionEndpoint={`tremfya-enrollment/${token}/complete`}
              onSuccess={onSuccess}
              isCompleted={isFormCompleted}
            />
          </>
        )}

        {/* TODO: show 404 error msg */}
        {isFormCompleted && authContext.auth && !form_data && <PageLoadError />}
      </BodyContainer>
    );
  }

  return (
    <Container>
      <TopNavbar />
      {body}
    </Container>
  );
};

export default TremfyaExpressEnrollmentLoggedOutTaskPage;
